'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, ComponentPropsWithoutRef, useEffect, useState } from 'react';
import { useCookieSettings } from '@/cookies';
import { EventService } from '@/shared/lib/event-service';
import { Button } from '@/shared/ui/button';
import { Center } from '@/shared/ui/center';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';

type CookiesOverlayWrapperProps = Omit<ComponentProps<typeof Layout>, 'element'>;
export const CookiesOverlayWrapper = ({ children, media, ...props }: CookiesOverlayWrapperProps) => {
    // TODO: Load also on server side from async cookies
    const cookieSettings = useCookieSettings((s) => s.cookieSettings);
    const [isClosing, setIsClosing] = useState(false);
    const [show, setShow] = useState(!cookieSettings.consentTimestamp);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        if (cookieSettings.consentTimestamp && show) {
            setIsClosing(true);
            setTimeout(() => {
                setShow(false);
            }, 400);
        }
    }, [cookieSettings.consentTimestamp]);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    if (!isMounted || !show) {
        return null;
    }

    return (
        <Layout
            element={Center}
            data-closing={isClosing}
            data-testid="cookies-overlay"
            media={{
                ...media,
                initial: {
                    position: 'fixed',
                    bottom: '1rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1,
                    opacity: 0,
                    animation: 'fade-in 0.4s ease-in-out forwards',

                    '&[data-closing=true]': {
                        animation: 'fade-out 0.4s ease-in-out forwards',
                    },
                    ...media?.initial,
                },
            }}
            {...props}
        >
            {children}
        </Layout>
    );
};

type CookiesOverlayBoldProps = Omit<ComponentProps<typeof Layout>, 'element'>;
export const CookiesOverlayBold = ({ ...props }: CookiesOverlayBoldProps) => {
    return <Layout element="b" {...props} />;
};

type CookiesOverlayLinkProps = Omit<ComponentProps<typeof Layout>, 'element'>;
export const CookiesOverlayLink = ({ media, ...props }: CookiesOverlayLinkProps) => {
    return (
        <Layout
            element="span"
            role="button"
            data-testid="cookies-overlay-privacy-policy-link"
            onClick={() => EventService.trigger('privacy-policy:view-detail', {})}
            media={{
                ...media,
                initial: {
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    ...media?.initial,
                },
            }}
            {...props}
        />
    );
};

type CookiesOverlayTextProps = Omit<ComponentProps<typeof Layout>, 'element'> & {
    linkProps?: Omit<CookiesOverlayLinkProps, 'children'>;
    boldProps?: Omit<CookiesOverlayBoldProps, 'children'>;
};
export const CookiesOverlayText = ({ linkProps, boldProps, media, ...props }: CookiesOverlayTextProps) => {
    const t = useTranslations();

    return (
        <Layout
            element="p"
            data-testid="cookies-overlay-text"
            media={{
                ...media,
                initial: {
                    fontSize: '0.75rem',
                    ...media?.initial,
                },
            }}
            {...props}
        >
            {t.rich('COOKIES_OVERLAY.TEXT', {
                b: (chunks) => <CookiesOverlayBold {...boldProps}>{chunks}</CookiesOverlayBold>,
                link: (chunks) => <CookiesOverlayLink {...linkProps}>{chunks}</CookiesOverlayLink>,
            })}
        </Layout>
    );
};

type CookiesOverlayButtonProps = Omit<ComponentProps<typeof Layout>, 'element'> &
    ComponentProps<typeof Button>;

export const CookiesOverlayAllowAllButton = ({ children, ...props }: CookiesOverlayButtonProps) => {
    return (
        <Layout
            element={Button}
            data-testid="cookies-overlay-allow-all-button"
            onClick={() => {
                const nowISODate = new Date().toISOString();
                EventService.trigger('cookies:update-consents', {
                    data: {
                        categories: { analytical: true, marketing: true },
                        consentTimestamp: nowISODate,
                        lastConsentTimestamp: nowISODate,
                    },
                });
            }}
            {...props}
        >
            {children ?? (
                <>
                    <CookiesOverlayAllowAllButtonIcon />
                    <CookiesOverlayAllowAllButtonText />
                </>
            )}
        </Layout>
    );
};

type CookiesOverlayIconProps = Omit<ComponentProps<typeof Layout>, 'element'>;
export const CookiesOverlayAllowAllButtonIcon = ({ media, ...props }: CookiesOverlayIconProps) => {
    return (
        <Layout
            element={(svgProps: ComponentPropsWithoutRef<'svg'>) => <Icon name="check" {...svgProps} />}
            media={{
                ...media,
                initial: {
                    width: '1.25rem',
                    height: '1.25rem',
                    ...media?.initial,
                },
            }}
            {...props}
        />
    );
};

type CookiesOverlayAllowAllButtonTextProps = Omit<ComponentProps<typeof Layout>, 'element'>;
export const CookiesOverlayAllowAllButtonText = (props: CookiesOverlayAllowAllButtonTextProps) => {
    const t = useTranslations();
    return (
        <Layout element="span" {...props}>
            {t('COOKIES_OVERLAY.ALLOW_ALL_BUTTON')}
        </Layout>
    );
};

export const CookiesOverlaySettingsButton = ({
    variant = 'secondary',
    ...props
}: CookiesOverlayButtonProps) => {
    const t = useTranslations();
    return (
        <Layout
            element={Button}
            variant={variant}
            data-testid="cookies-overlay-settings-button"
            onClick={() => EventService.trigger('cookies:view-detail')}
            {...props}
        >
            {t('COOKIES_OVERLAY.SETTINGS_BUTTON')}
        </Layout>
    );
};
